import {useEffect, useId, useRef, useState} from "react";
import {useStore} from "react-redux";
import {css} from "@panda/css";

import {ResponsiveModal} from "@pg-design/modal-module";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {IRootState} from "../../../store/store";
import {advanceRegistrationFlow} from "../../actions/advance_registration_flow";
import {completeRegistrationWizardSmsLogin} from "../../actions/complete_registration_wizard_sms_login";
import {REGISTRATION_WIZARD_STEPS} from "../../constants/registration_wizard_steps";
import {useDeferredModalClose} from "../../hooks/use_deferred_modal_close";
import {useRegistrationWizardTrackingContext} from "../../hooks/use_registration_wizard_tracking_context";
import {getPreviousRegistrationWizardStep, goToPreviousStep} from "../../state/registration_flow_slice";
import {
    getRegistrationWizardPhoneVerificationContext,
    trackRegistrationWizardButtonClick,
    trackRegistrationWizardFormView,
    trackRegistrationWizardPhoneAbandoned,
    trackRegistrationWizardStepView
} from "../../tracking/track_registration_wizard_events";
import {RegistrationFlowStep} from "../../types/RegistrationFlowStep";
import {RegistrationWizardEmailStep} from "./RegistrationWizardEmailStep";
import {RegistrationWizardHeader} from "./RegistrationWizardHeader";
import {RegistrationWizardNameStep} from "./RegistrationWizardNameStep";
import {RegistrationWizardPhoneStep} from "./RegistrationWizardPhoneStep";
import {RegistrationWizardSmsStep} from "./RegistrationWizardSmsStep";

interface IProps {
    zIndex?: number;
    onClose: () => void;
}

export const RegistrationWizard = (props: IProps) => {
    const {zIndex, onClose} = props;
    const dispatch = useAppDispatch();
    const store = useStore<IRootState>();
    const {currentStep, visitedSteps, runId} = useAppSelector((state) => state.registrationFlow);
    const [openedRunId] = useState(runId);
    const titleId = useId();
    const stepIndicatorId = useId();
    const trackingContext = useRegistrationWizardTrackingContext();
    const phoneVerifiedContext = getRegistrationWizardPhoneVerificationContext(trackingContext);
    const previousStep = getPreviousRegistrationWizardStep(currentStep, visitedSteps);
    const wizardStep = REGISTRATION_WIZARD_STEPS.find(({step}) => step === currentStep);
    const stepToken = wizardStep?.token ?? null;
    const [advancingStep, setAdvancingStep] = useState<RegistrationFlowStep | null>(null);
    const [isSmsCodeBusy, setIsSmsCodeBusy] = useState(false);
    const isAdvancing = advancingStep !== null;
    const isPhoneVerificationBusy = currentStep === RegistrationFlowStep.PHONE_VERIFICATION && (isAdvancing || isSmsCodeBusy);
    const isStepDisabled = advancingStep === currentStep && currentStep !== RegistrationFlowStep.PHONE_VERIFICATION;
    const isFormViewTrackedRef = useRef(false);

    const closeOpenedRun = () => {
        const flowState = store.getState().registrationFlow;

        if (flowState.isActive && flowState.runId === openedRunId) {
            onClose();
        }
    };

    const {isOpen, requestClose, handleAfterClose} = useDeferredModalClose(closeOpenedRun);

    useEffect(() => {
        if (stepToken === "auth") {
            if (!isFormViewTrackedRef.current) {
                isFormViewTrackedRef.current = true;
                trackRegistrationWizardFormView(trackingContext);
            }

            return;
        }

        if (stepToken) {
            trackRegistrationWizardStepView(trackingContext, stepToken);
        }
    }, [currentStep]);

    useEffect(() => {
        const dialog = document.getElementById(stepIndicatorId)?.closest<HTMLElement>('[role="dialog"]');

        if (dialog && !dialog.contains(document.activeElement)) {
            dialog.focus({preventScroll: true});
        }
    }, [currentStep]);

    const handleClose = () => {
        trackRegistrationWizardButtonClick(trackingContext, "rp_auth_close", stepToken);

        if (isAdvancing) {
            closeOpenedRun();
            return;
        }

        if (currentStep === RegistrationFlowStep.PHONE_VERIFICATION) {
            trackRegistrationWizardPhoneAbandoned(trackingContext);
        }

        requestClose();
    };

    const advance = async (isSmsLogin = false) => {
        const startedStep = currentStep;
        setAdvancingStep(startedStep);

        try {
            await dispatch(isSmsLogin ? completeRegistrationWizardSmsLogin() : advanceRegistrationFlow());
        } finally {
            setAdvancingStep((step) => (step === startedStep ? null : step));
        }
    };

    const handleBack = () => {
        trackRegistrationWizardButtonClick(trackingContext, "rp_auth_back", stepToken);
        dispatch(goToPreviousStep());
    };

    const renderStep = () => {
        switch (currentStep) {
            case RegistrationFlowStep.REGISTRATION:
                return <RegistrationWizardEmailStep titleId={titleId} onAdvance={() => advance()} />;
            case RegistrationFlowStep.PHONE_COMPLETION:
                return <RegistrationWizardPhoneStep titleId={titleId} onAdvance={() => advance()} />;
            case RegistrationFlowStep.PHONE_VERIFICATION:
                return (
                    <RegistrationWizardSmsStep titleId={titleId} amplitudeContext={phoneVerifiedContext} onVerified={advance} onBusyChange={setIsSmsCodeBusy} />
                );
            case RegistrationFlowStep.NAME_COMPLETION:
                return <RegistrationWizardNameStep titleId={titleId} onAdvance={() => advance()} />;
            default:
                return null;
        }
    };

    return (
        <ResponsiveModal
            isOpen={isOpen}
            onClose={handleClose}
            onAfterClose={handleAfterClose}
            shouldCloseOnOverlayClick={false}
            zIndex={zIndex}
            ariaLabelledBy={titleId}
            ariaDescribedBy={stepIndicatorId}
            className={modalStyle}
            dataTestId={OFFER_TEST_IDS.OFFER_VIEW.REGISTER_FORM_MODAL}
        >
            <RegistrationWizardHeader
                stepIndicatorId={stepIndicatorId}
                stepNumber={wizardStep?.number ?? 1}
                onBack={previousStep && !isPhoneVerificationBusy ? handleBack : null}
            />
            <ResponsiveModal.Body>
                <fieldset disabled={isStepDisabled} className={stepFieldsetStyle}>
                    {renderStep()}
                </fieldset>
            </ResponsiveModal.Body>
        </ResponsiveModal>
    );
};

const stepFieldsetStyle = css({
    minWidth: 0,
    m: 0,
    p: 0,
    border: "none"
});

const modalStyle = css({
    "--responsive-modal-width": "47.9rem"
});
import {useEffect, useId, useRef, useState} from "react";
import {useStore} from "react-redux";
import {css} from "@panda/css";

import {ResponsiveModal} from "@pg-design/modal-module";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {IRootState} from "../../../store/store";
import {advanceRegistrationFlow} from "../../actions/advance_registration_flow";
import {completeRegistrationWizardSmsLogin} from "../../actions/complete_registration_wizard_sms_login";
import {REGISTRATION_WIZARD_STEPS} from "../../constants/registration_wizard_steps";
import {useDeferredModalClose} from "../../hooks/use_deferred_modal_close";
import {useRegistrationWizardTrackingContext} from "../../hooks/use_registration_wizard_tracking_context";
import {getPreviousRegistrationWizardStep, goToPreviousStep} from "../../state/registration_flow_slice";
import {
    getRegistrationWizardPhoneVerificationContext,
    trackRegistrationWizardButtonClick,
    trackRegistrationWizardFormView,
    trackRegistrationWizardPhoneAbandoned,
    trackRegistrationWizardStepView
} from "../../tracking/track_registration_wizard_events";
import {RegistrationFlowStep} from "../../types/RegistrationFlowStep";
import {RegistrationWizardEmailStep} from "./RegistrationWizardEmailStep";
import {RegistrationWizardHeader} from "./RegistrationWizardHeader";
import {RegistrationWizardNameStep} from "./RegistrationWizardNameStep";
import {RegistrationWizardPhoneStep} from "./RegistrationWizardPhoneStep";
import {RegistrationWizardSmsStep} from "./RegistrationWizardSmsStep";

interface IProps {
    zIndex?: number;
    onClose: () => void;
}

export const RegistrationWizard = (props: IProps) => {
    const {zIndex, onClose} = props;
    const dispatch = useAppDispatch();
    const store = useStore<IRootState>();
    const {currentStep, visitedSteps, runId} = useAppSelector((state) => state.registrationFlow);
    const [openedRunId] = useState(runId);
    const titleId = useId();
    const stepIndicatorId = useId();
    const trackingContext = useRegistrationWizardTrackingContext();
    const phoneVerifiedContext = getRegistrationWizardPhoneVerificationContext(trackingContext);
    const previousStep = getPreviousRegistrationWizardStep(currentStep, visitedSteps);
    const wizardStep = REGISTRATION_WIZARD_STEPS.find(({step}) => step === currentStep);
    const stepToken = wizardStep?.token ?? null;
    const [advancingStep, setAdvancingStep] = useState<RegistrationFlowStep | null>(null);
    const [isSmsCodeBusy, setIsSmsCodeBusy] = useState(false);
    const isAdvancing = advancingStep !== null;
    const isPhoneVerificationBusy = currentStep === RegistrationFlowStep.PHONE_VERIFICATION && (isAdvancing || isSmsCodeBusy);
    const isStepDisabled = advancingStep === currentStep && currentStep !== RegistrationFlowStep.PHONE_VERIFICATION;
    const isFormViewTrackedRef = useRef(false);

    const closeOpenedRun = () => {
        const flowState = store.getState().registrationFlow;

        if (flowState.isActive && flowState.runId === openedRunId) {
            onClose();
        }
    };

    const {isOpen, requestClose, handleAfterClose} = useDeferredModalClose(closeOpenedRun);

    useEffect(() => {
        if (stepToken === "auth") {
            if (!isFormViewTrackedRef.current) {
                isFormViewTrackedRef.current = true;
                trackRegistrationWizardFormView(trackingContext);
            }

            return;
        }

        if (stepToken) {
            trackRegistrationWizardStepView(trackingContext, stepToken);
        }
    }, [currentStep]);

    useEffect(() => {
        const dialog = document.getElementById(stepIndicatorId)?.closest<HTMLElement>('[role="dialog"]');

        if (dialog && !dialog.contains(document.activeElement)) {
            dialog.focus({preventScroll: true});
        }
    }, [currentStep]);

    const handleClose = () => {
        trackRegistrationWizardButtonClick(trackingContext, "rp_auth_close", stepToken);

        if (isAdvancing) {
            closeOpenedRun();
            return;
        }

        if (currentStep === RegistrationFlowStep.PHONE_VERIFICATION) {
            trackRegistrationWizardPhoneAbandoned(trackingContext);
        }

        requestClose();
    };

    const advance = async (isSmsLogin = false) => {
        const startedStep = currentStep;
        setAdvancingStep(startedStep);

        try {
            await dispatch(isSmsLogin ? completeRegistrationWizardSmsLogin() : advanceRegistrationFlow());
        } finally {
            setAdvancingStep((step) => (step === startedStep ? null : step));
        }
    };

    const handleBack = () => {
        trackRegistrationWizardButtonClick(trackingContext, "rp_auth_back", stepToken);
        dispatch(goToPreviousStep());
    };

    const renderStep = () => {
        switch (currentStep) {
            case RegistrationFlowStep.REGISTRATION:
                return <RegistrationWizardEmailStep titleId={titleId} onAdvance={() => advance()} />;
            case RegistrationFlowStep.PHONE_COMPLETION:
                return <RegistrationWizardPhoneStep titleId={titleId} onAdvance={() => advance()} />;
            case RegistrationFlowStep.PHONE_VERIFICATION:
                return (
                    <RegistrationWizardSmsStep titleId={titleId} amplitudeContext={phoneVerifiedContext} onVerified={advance} onBusyChange={setIsSmsCodeBusy} />
                );
            case RegistrationFlowStep.NAME_COMPLETION:
                return <RegistrationWizardNameStep titleId={titleId} onAdvance={() => advance()} />;
            default:
                return null;
        }
    };

    return (
        <ResponsiveModal
            isOpen={isOpen}
            onClose={handleClose}
            onAfterClose={handleAfterClose}
            shouldCloseOnOverlayClick={false}
            zIndex={zIndex}
            ariaLabelledBy={titleId}
            ariaDescribedBy={stepIndicatorId}
            className={modalStyle}
            dataTestId={OFFER_TEST_IDS.OFFER_VIEW.REGISTER_FORM_MODAL}
        >
            <RegistrationWizardHeader
                stepIndicatorId={stepIndicatorId}
                stepNumber={wizardStep?.number ?? 1}
                onBack={previousStep && !isPhoneVerificationBusy ? handleBack : null}
            />
            <ResponsiveModal.Body>
                <fieldset disabled={isStepDisabled} className={stepFieldsetStyle}>
                    {renderStep()}
                </fieldset>
            </ResponsiveModal.Body>
        </ResponsiveModal>
    );
};

const stepFieldsetStyle = css({
    minWidth: 0,
    m: 0,
    p: 0,
    border: "none"
});

const modalStyle = css({
    "--responsive-modal-width": "47.9rem"
});
